// Primary tab strip for the merged analytics surface — exactly two
// pills, mirroring the App-Store-style segmented control:
//
//   Projects  → /analytics             (per-project / site visitor analytics
//                                       family: Overview + Map + Logs — the
//                                       secondary view strip lives in
//                                       AnalyticsHeader, not here)
//   Workspace → /workspace-analytics   (workspace usage dashboard)
//
// Family classification (2026-08-11 division per Nika "divide all ws
// specific analytics"): the geo MAP is visitor/project data → Projects
// wherever its URL lives; the LOGS diagnostic feed is workspace
// telemetry → Workspace wherever its URL lives (canonical
// /workspace-analytics/logs/*, plus the /analytics/logs/* and bare
// /logs aliases).
//
// Active state derives from `root.currentPath` (set by AppShell's
// locationchange patch) or `root._canvasSlot` (in-PageSlot mode), so
// clicks route through `el.router`/slot updates and the highlight
// follows reactively.

import { stripWorkspacePrefix } from '../functions/workspacePrefix.js'

export const AnalyticsNavbar = {
  // FA204 / frank trap — TABS + activeKey used to be bare module-scope
  // const/function read from inside `Tabs.children` (a serialized factory);
  // they read as undefined once this component was frank.toJSON'd for
  // platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Moved into `scope`, called
  // via `el.scope.<name>`.
  scope: {
    TABS: [
      { key: 'projects', label: 'editor.analyticsNavbar.projects', route: '/analytics' },
      {
        key: 'workspace',
        label: 'editor.analyticsNavbar.workspace',
        route: '/workspace-analytics'
      }
    ],

    activeKey(root) {
      // In the workspace's home PageSlot the URL stays on '/', so the active
      // route is the slot value; standalone analytics has it undefined and
      // falls back to the real path. Strip any `/w/<org-slug>` prefix so the
      // routes still classify under multi-workspace tabs (§6d).
      const path = stripWorkspacePrefix(
        root?._canvasSlot ||
          root?.currentPath ||
          (typeof window !== 'undefined' ? window.location.pathname : '/')
      ).split('?')[0]
      // Logs classify under Workspace on every alias; the map stays a
      // Projects view even at its legacy /workspace-analytics/map URL.
      if (path.includes('/logs')) return 'workspace'
      if (path.startsWith('/workspace-analytics/map')) return 'projects'
      if (path === '/workspace-analytics' || path.startsWith('/workspace-analytics/'))
        return 'workspace'
      return 'projects'
    }
  },
  flow: 'x',
  align: 'center center',
  gap: 'V',
  padding: 'A B',
  // Chrome hairline under the tab strip (design-direction.md §8 /
  // DESIGN_LANGUAGE §2.5 — the exact PageHeader `borderBottomWidth: hairline`
  // pattern the doc names for retirement) — removed; the page content below
  // separates by its own surface, not a stroke here.

  Tabs: {
    flow: 'x',
    align: 'center',
    gap: 'V',
    padding: 'V',
    round: 'radiusPill',
    background: 'overlay',
    // Card-outline on top of an already-opaque `overlay` fill — the fill
    // alone separates the pill strip (same redundant-stroke pattern as
    // tickets/Cards.js's fix).
    // Re-run `children` when the route changes so each tab's state carries a
    // fresh `active` flag (see the note on childExtends below). `_canvasSlot`
    // is the in-PageSlot equivalent of a navigation.
    subscribeTo: ['currentPath', '_canvasSlot'],
    childrenAs: 'state',
    // Resolve active ONCE per render and hand it to each child as state,
    // rather than having every child re-derive it from `s.root`.
    children: (el, s) => {
      const active = el.scope.activeKey(s.root)
      return el.scope.TABS.map((t) => ({ ...t, active: t.key === active }))
    },
    childExtends: {
      tag: 'button',
      type: 'button',
      attr: { role: 'tab' },
      minHeight: 'controlHCompact',
      padding: '0 Z',
      round: 'radiusPill',
      border: '0',
      cursor: 'pointer',
      fontSize: 'Z',
      fontWeight: '700',
      whiteSpace: 'nowrap',
      transition: '160ms defaultBezier background, 160ms defaultBezier color',
      // Active paint goes through the CONDITIONAL CLASS, not a style factory.
      // Measured 2026-08-08: with `background`/`color` as `(el, s) =>` factories
      // reading `s.root`, all tabs rendered byte-identical class lists with
      // NO background class at all — the factories evaluated once against a
      // childrenAs child and froze. The parent recomputes `active` into child
      // state AND the paint hangs off `.isActive`, so whichever mechanism the
      // runtime honours, the strip is correct.
      background: 'transparent',
      color: 'caption',
      isActive: (el, s) => !!s.active,
      '.isActive': { background: 'selected', color: 'title' },
      ariaSelected: (el, s) => (s.active ? 'true' : 'false'),
      ':hover': { color: 'title' },
      text: (el, s) => el.call('polyglot', s.label || ''),
      onClick: (e, el, s) => {
        e.preventDefault()
        if (s.active) return
        // Inside the workspace's home AppWindow (PageSlot) swap the slot
        // content in-place — `el.router` would navigate to a full page and
        // leave the PageSlot, blanking the layout (2026-06-09 per Nika
        // "switching tabs should stay in PageSlot"). The slot routes are
        // registered in the workspace's _APP_WINDOW_PAGES.
        if (s.root?._canvasSlot) {
          s.root.update({ _canvasSlot: s.route })
          return
        }
        el.router(s.route, el.getRoot())
      }
    }
  }
}
